import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Menu } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import NotificationBell from "@/components/NotificationBell";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";

export default function Layout({ children, title, subtitle, actions, eyebrow, fill = false }) {
  const { workspaceId } = useParams();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const hasHeader = title || actions || eyebrow || subtitle;

  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);

  return (
    <div className="editorial-canvas app-shell flex overflow-hidden">
      <div className="hidden lg:block w-56 shrink-0 border-r border-slate-200">
        <Sidebar activeWorkspaceId={workspaceId || null} />
      </div>
      <main className="flex-1 min-w-0 h-full flex flex-col">
        <div className="lg:hidden flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-white">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="btn-ghost inline-flex items-center gap-2" data-testid="open-navigation">
                <Menu className="w-4 h-4" /> Menu
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 max-w-[90vw]">
              <SheetTitle className="sr-only">Client navigation</SheetTitle>
              <SheetDescription className="sr-only">Choose a client or a workspace page.</SheetDescription>
              <Sidebar activeWorkspaceId={workspaceId || null} />
            </SheetContent>
          </Sheet>
          <span className="text-sm font-semibold text-slate-700">The LinkedIn Lab</span>
        </div>
        {hasHeader && (
          <header data-testid="topbar" className="ed-topbar shrink-0 px-4 sm:px-6 xl:px-8 pt-5 sm:pt-7 pb-5">
            <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-end 2xl:justify-between">
              <div className="min-w-0">
                {eyebrow && <div className="chapter-eyebrow mb-2">{eyebrow}</div>}
                {title && <h1 className="chapter-title app-page-title" data-testid="page-title">{title}</h1>}
                {subtitle && <p className="chapter-sub mt-2">{subtitle}</p>}
              </div>
              <div className="app-header-actions flex flex-wrap items-center gap-2 min-w-0">
                {actions}
                <NotificationBell />
              </div>
            </div>
          </header>
        )}
        <div className={fill
          ? "flex-1 min-h-0 min-w-0 overflow-hidden px-4 sm:px-6 xl:px-8 py-4 flex flex-col"
          : "flex-1 min-h-0 min-w-0 overflow-y-auto px-4 sm:px-6 xl:px-8 py-6"}>
          {children}
        </div>
      </main>
      <Toaster theme="light" position="bottom-right" />
    </div>
  );
}
